import { useMemo } from 'react';
import { useQuery, QueryResult } from '@apollo/client';

import { useUserSegments } from 'hooks/use-user-segments';
import { GetMenuSectionsDocument, GetMenuSectionsQuery } from 'src/gql/graphql';

type UseMenuSectionsOptions = {
  dispensaryId?: string;
  skip?: boolean;
};

type QueryHookReturn = QueryResult<GetMenuSectionsQuery>;

type UseMenuSectionsReturn = {
  menuSections: NonNullable<QueryHookReturn['data']>['getMenuSections'];
  loading: boolean;
  error: QueryHookReturn['error'];
  refetch: QueryHookReturn['refetch'];
};

export default function useMenuSections({ dispensaryId, skip = false }: UseMenuSectionsOptions): UseMenuSectionsReturn {
  const { segments: userSegments, loading: segmentsLoading } = useUserSegments({ dispensaryId });
  const sortedSegments = useMemo(() => [...userSegments].sort(), [userSegments]);
  const userSegmentIds = sortedSegments.length > 0 ? sortedSegments : undefined;

  const { data, loading, error, refetch } = useQuery(GetMenuSectionsDocument, {
    variables: {
      dispensaryId: dispensaryId ?? '',
      userSegmentIds,
    },
    skip: skip || !dispensaryId || segmentsLoading,
    fetchPolicy: 'cache-and-network',
  });

  return {
    menuSections: data?.getMenuSections ?? [],
    loading: skip ? false : loading || segmentsLoading,
    error,
    refetch,
  };
}
